/**
 * Warranty display helpers for My Watches.
 *
 * The list endpoint returns each watch with `start_date` (activation) and
 * `end_date` (valid-until). The card badge shows ACTIVE when the warranty has
 * not yet expired. (The live warranty *status code* — see WarrantyStatus — is
 * only returned by the separate warranty-lookup endpoint, not the list.)
 */

import type { Watch } from './types';

export interface WarrantyInfo {
  /** Warranty has not yet expired (end date in the future). */
  isActive: boolean;
  /** Activation date string from the backend, if present. */
  startDate: string | null;
  /** Valid-until date string from the backend, if present. */
  endDate: string | null;
}

/**
 * Derive warranty display info from a watch record.
 *
 * `now` is injectable so the derivation is deterministic in tests.
 */
export function getWarrantyInfo(
  watch: Pick<Watch, 'start_date' | 'end_date'>,
  now: number = Date.now()
): WarrantyInfo {
  const startDate = watch.start_date ?? null;
  const endDate = watch.end_date ?? null;

  const endMs = endDate ? new Date(endDate).getTime() : NaN;
  const isActive = Number.isNaN(endMs) ? false : endMs > now;

  return { isActive, startDate, endDate };
}
